// AuthContext — single source of truth para el usuario logueado.
// El token vive en localStorage. Al montar la app se hace GET /auth/me para
// re-hidratar el user si ya hay token.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthResponse, User } from '@bini-trading/shared-types';
import { api, tokenStorage } from '../lib/api';

interface AuthContextValue {
  user: User | null;
  loading: boolean; // true mientras se intenta re-hidratar al montar
  signup: (input: { email: string; password: string; name: string }) => Promise<void>;
  login: (input: { email: string; password: string }) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Re-hidratación inicial: si hay token, validarlo contra /auth/me.
  useEffect(() => {
    const token = tokenStorage.get();
    if (!token) {
      setLoading(false);
      return;
    }
    api<User>('/auth/me')
      .then((u) => setUser(u))
      .catch(() => tokenStorage.clear())
      .finally(() => setLoading(false));
  }, []);

  const signup = useCallback(async (input: { email: string; password: string; name: string }) => {
    const res = await api<AuthResponse>('/auth/signup', { method: 'POST', body: input });
    tokenStorage.set(res.token);
    setUser(res.user);
  }, []);

  const login = useCallback(async (input: { email: string; password: string }) => {
    const res = await api<AuthResponse>('/auth/login', { method: 'POST', body: input });
    tokenStorage.set(res.token);
    setUser(res.user);
  }, []);

  const logout = useCallback(() => {
    tokenStorage.clear();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, signup, login, logout }),
    [user, loading, signup, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
